import React from "react";
import { useNavigate } from "react-router-dom";
import { completeNewPasswordChallenge } from "../../../../aws/auth";
import { useAuth } from "../../../../aws/AuthContext";
import toast from "react-hot-toast";
import { DASHBOARD_ROUTE } from "app/constants/route";

type Props = {
  username: string;
};

const initialValues = {
  formValue: {
    newPassword: "",
    confirmPassword: "",
  },
};

const useForcePasswordChangeForm = ({ username }: Props) => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const [isLoading, setIsLoading] = React.useState(false);
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [formValue, setFormValue] = React.useState(initialValues?.formValue);

  const updateFormValue = (key: string, value: any) => {
    try {
      const newFormValue = { ...formValue, [key]: value };
      setFormValue(newFormValue);
    } catch (err) {
      console.error("Error updating form value:", err);
    }
  };

  const passwordsMatch = formValue.newPassword === formValue.confirmPassword;

  const completePasswordChange = async () => {
    if (isLoading) return;

    // Validate passwords match
    if (!passwordsMatch) {
      toast.error("Passwords do not match!");
      return;
    }

    // Validate password strength
    if (formValue.newPassword.length < 8) {
      toast.error("Password must be at least 8 characters long!");
      return;
    }

    try {
      setIsLoading(true);

      const result = await completeNewPasswordChallenge(formValue.newPassword);

      if (result.success) {
        // Refresh auth state
        await refreshUser();
        
        // Navigate to dashboard
        setTimeout(() => {
          navigate(DASHBOARD_ROUTE);
        }, 1000);
      }
    } catch (e) {
      console.error("Complete password change error:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await completePasswordChange();
  };

  return {
    showNewPassword,
    setShowNewPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    formValue,
    updateFormValue,
    isLoading,
    onSubmit,
    passwordsMatch,
  };
};

export default useForcePasswordChangeForm;
